// `/sala` (5.6): escolha da sala, estado ao vivo e leitura da pulseira para entrada/saída.
// O servidor decide a ação (5.3); a tela só envia sala + código e reage ao `action`.
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Ban,
  BellRing,
  ChevronRight,
  DoorOpen,
  Eye,
  EyeOff,
  HeartCrack,
  Repeat,
  SprayCan
} from "lucide-react";
import {
  roomStateLabels,
  type DeviceSession,
  type RoomDeviceRoom,
  type RoomStay,
  type ScanResponse
} from "@centro-cirurgico/contracts";
import { Badge, ConfirmDialog, Empty, Modal, Notice, formatDuration, formatTime } from "@centro-cirurgico/ui";

import { ClinicalFlow } from "../components/ClinicalFlow";
import { DeviceShell } from "../components/DeviceShell";
import { ModeSwitch, readDeviceMode, storeDeviceMode, type DeviceMode } from "../components/ModeSwitch";
import { ErrorState } from "../components/ErrorState";
import { LoadingState } from "../components/LoadingState";
import { RequireDeviceSession } from "../components/RequireDeviceSession";
import { WristbandScanner, type WristbandScannerHandle } from "../components/WristbandScanner";
import { hasFacialCapability, useCapabilities } from "../hooks/useCapabilities";
import { useKeyboardWedge } from "../hooks/useKeyboardWedge";
import {
  describeScanResult,
  exitConfirmedFeedback,
  scanErrorFeedback,
  type ScanFeedback
} from "../components/scan-actions";
import {
  callCompanion,
  confirmExit,
  errorMessage,
  finishSetup,
  getRoomDeviceRoom,
  isHttpError,
  listRoomDeviceRooms,
  registerDeath,
  scanWristband
} from "../utils/api";
import { clearStoredRoomId, readStoredRoomId, storeRoomId } from "../utils/session";

const REFRESH_MS = 30_000;
const TICK_MS = 30_000;
const PIN_PATTERN = /^\d{4,8}$/;

export function RoomPage() {
  return <RequireDeviceSession mode="room">{(session) => <RoomScreen session={session} />}</RequireDeviceSession>;
}

function RoomScreen({ session }: { session: DeviceSession }) {
  const [roomId, setRoomId] = useState<string | null>(() => readStoredRoomId());

  // Link de Sala preso a uma sala: não há escolha nem troca de sala neste dispositivo.
  if (session.roomId) {
    return <RoomDevice key={session.roomId} session={session} roomId={session.roomId} />;
  }

  if (!roomId) {
    return (
      <RoomPicker
        session={session}
        onSelect={(id) => {
          storeRoomId(id);
          setRoomId(id);
        }}
      />
    );
  }

  return (
    <RoomDevice
      key={roomId}
      session={session}
      roomId={roomId}
      onChangeRoom={() => {
        clearStoredRoomId();
        setRoomId(null);
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Escolha da sala (lista agrupada por setor)
// ---------------------------------------------------------------------------

function RoomPicker({ session, onSelect }: { session: DeviceSession; onSelect: (roomId: string) => void }) {
  const roomsQuery = useQuery({ queryKey: ["room-device", "rooms"], queryFn: listRoomDeviceRooms });

  const groups = useMemo(() => {
    const map = new Map<string, RoomDeviceRoom[]>();
    for (const room of roomsQuery.data ?? []) {
      const list = map.get(room.sectorName) ?? [];
      list.push(room);
      map.set(room.sectorName, list);
    }
    return [...map.entries()];
  }, [roomsQuery.data]);

  return (
    <DeviceShell tag="SALA" title={session.hospitalName} subtitle={session.label}>
      <div className="room-picker">
        <div className="totem-setup-heading">
          <h1>Em qual sala este dispositivo está?</h1>
          <p>A escolha fica salva neste aparelho. Para mudar depois, use "Trocar de sala" no cabeçalho.</p>
        </div>
        {roomsQuery.isPending ? (
          <LoadingState message="Carregando salas…" />
        ) : roomsQuery.isError ? (
          <ErrorState
            title="Não foi possível carregar as salas"
            message={errorMessage(roomsQuery.error)}
            onRetry={() => void roomsQuery.refetch()}
          />
        ) : groups.length === 0 ? (
          <Empty title="Nenhuma sala ativa" icon={<DoorOpen size={32} />}>
            Cadastre e ative as salas desta unidade no painel administrativo.
          </Empty>
        ) : (
          groups.map(([sector, rooms]) => (
            <section key={sector} className="room-picker-group" aria-labelledby={`sector-${sector}`}>
              <h2 className="section-title" id={`sector-${sector}`}>
                {sector}
              </h2>
              <div className="service-grid">
                {rooms.map((room) => (
                  <button key={room.id} type="button" className="service-card" onClick={() => onSelect(room.id)}>
                    <Badge value={room.state} label={roomStateLabels[room.state]} />
                    <h3>{room.name}</h3>
                    <small>
                      {room.unitName} · {room.sectorName}
                    </small>
                    <ChevronRight aria-hidden="true" size={22} />
                  </button>
                ))}
              </div>
            </section>
          ))
        )}
      </div>
    </DeviceShell>
  );
}

// ---------------------------------------------------------------------------
// Operação da sala
// ---------------------------------------------------------------------------

interface NameDialogState {
  code: string;
  format: string | undefined;
}

interface RoomDeviceProps {
  session: DeviceSession;
  roomId: string;
  /** Ausente quando o link é de uma sala fixa (sem "Trocar de sala"). */
  onChangeRoom?: () => void;
}

function minutesBetween(fromIso: string | null, toMs: number): number | null {
  if (!fromIso) return null;
  const from = Date.parse(fromIso);
  if (!Number.isFinite(from)) return null;
  return Math.max(0, (toMs - from) / 60_000);
}

function RoomDevice({ session, roomId, onChangeRoom }: RoomDeviceProps) {
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ["room-device", "room", roomId] as const, [roomId]);
  const scannerRef = useRef<WristbandScannerHandle | null>(null);

  const roomQuery = useQuery({
    queryKey,
    queryFn: () => getRoomDeviceRoom(roomId),
    refetchInterval: REFRESH_MS,
    retry: (count, error) => !(isHttpError(error) && error.status === 404) && count < 2
  });
  const room = roomQuery.data ?? null;

  // Relógio: horários sempre do servidor (5.8). Guardamos o deslocamento e "ticamos" a cada 30 s.
  const [serverOffset, setServerOffset] = useState(0);
  const [tick, setTick] = useState(() => Date.now());
  useEffect(() => {
    if (!room?.serverTime) return;
    const parsed = Date.parse(room.serverTime);
    if (Number.isFinite(parsed)) setServerOffset(parsed - Date.now());
    setTick(Date.now());
  }, [room?.serverTime]);
  useEffect(() => {
    const interval = window.setInterval(() => setTick(Date.now()), TICK_MS);
    return () => window.clearInterval(interval);
  }, []);
  const serverNow = tick + serverOffset;

  const applyRoom = useCallback(
    (next: RoomDeviceRoom) => {
      queryClient.setQueryData(queryKey, next);
    },
    [queryClient, queryKey]
  );
  const refresh = useCallback(() => queryClient.invalidateQueries({ queryKey }), [queryClient, queryKey]);

  // Diálogos e avisos
  const [nameDialog, setNameDialog] = useState<NameDialogState | null>(null);
  const [exitDialog, setExitDialog] = useState<RoomStay | null>(null);
  const [deathDialog, setDeathDialog] = useState(false);
  const [switchDialog, setSwitchDialog] = useState(false);
  const [notice, setNotice] = useState<{ error?: string | null; success?: string | null; warning?: string | null }>({});
  const [showFullName, setShowFullName] = useState(true);
  const [busyAction, setBusyAction] = useState<"urgency" | "setup" | null>(null);
  const [nextAllowedAt, setNextAllowedAt] = useState<string | null>(null);

  const dialogOpen = Boolean(nameDialog || exitDialog || deathDialog || switchDialog);

  // Mesmo aparelho para a pulseira do paciente e a validação facial da equipe.
  const { data: capabilities } = useCapabilities();
  const facialEnabled = hasFacialCapability(capabilities?.capabilities);
  const [mode, setMode] = useState<DeviceMode>(() => readDeviceMode());
  const activeMode: DeviceMode = facialEnabled ? mode : "wristband";
  const changeMode = useCallback((next: DeviceMode) => {
    storeDeviceMode(next);
    setMode(next);
  }, []);

  const handleScanResponse = useCallback(
    (response: ScanResponse, code: string, format: string | undefined): ScanFeedback | null => {
      applyRoom(response.room);
      const outcome = describeScanResult(response.action, response);
      if (outcome.dialog === "name_required") {
        setNameDialog({ code, format });
        return null;
      }
      if (outcome.dialog === "confirm_exit") {
        const stay = response.stay ?? response.room.openStay;
        if (!stay) return scanErrorFeedback("Não há paciente em sala para registrar a saída.");
        setExitDialog(stay);
        return null;
      }
      return outcome.feedback;
    },
    [applyRoom]
  );

  const handleScan = useCallback(
    async (code: string, format?: string): Promise<ScanFeedback | null> => {
      try {
        const response = await scanWristband(roomId, format ? { code, format } : { code });
        return handleScanResponse(response, code, format);
      } catch (error) {
        void refresh();
        return scanErrorFeedback(errorMessage(error));
      }
    },
    [roomId, handleScanResponse, refresh]
  );

  // No modo facial, uma leitura do leitor físico de pulseira volta sozinha para o modo pulseira.
  useKeyboardWedge(activeMode === "face" && !dialogOpen, (code) => {
    changeMode("wristband");
    void handleScan(code).then((feedback) => {
      if (!feedback) return;
      if (scannerRef.current) scannerRef.current.showFeedback(feedback);
      else if (feedback.tone === "error") setNotice({ error: feedback.title });
      else setNotice({ success: feedback.title });
    });
  });

  // Entrada rápida (pulseira sem cadastro): reenvia a leitura com o nome do paciente.
  const [quickName, setQuickName] = useState("");
  const [quickNameError, setQuickNameError] = useState<string | null>(null);
  const [quickNameBusy, setQuickNameBusy] = useState(false);
  async function submitQuickName(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!nameDialog || quickNameBusy) return;
    const patientName = quickName.trim();
    if (patientName.length < 3) {
      setQuickNameError("Informe o nome completo do paciente.");
      return;
    }
    setQuickNameBusy(true);
    setQuickNameError(null);
    try {
      const response = await scanWristband(
        roomId,
        nameDialog.format
          ? { code: nameDialog.code, format: nameDialog.format, patientName }
          : { code: nameDialog.code, patientName }
      );
      const feedback = handleScanResponse(response, nameDialog.code, nameDialog.format);
      setNameDialog(null);
      setQuickName("");
      if (feedback) scannerRef.current?.showFeedback(feedback);
    } catch (error) {
      setQuickNameError(errorMessage(error));
    } finally {
      setQuickNameBusy(false);
    }
  }
  function cancelQuickName() {
    setNameDialog(null);
    setQuickName("");
    setQuickNameError(null);
    scannerRef.current?.reset();
  }

  // Confirmação da saída
  const [exitBusy, setExitBusy] = useState(false);
  const [exitError, setExitError] = useState<string | null>(null);
  async function confirmExitNow() {
    if (!exitDialog || exitBusy) return;
    setExitBusy(true);
    setExitError(null);
    try {
      const response = await confirmExit(exitDialog.id);
      applyRoom(response.room);
      setExitDialog(null);
      scannerRef.current?.showFeedback(exitConfirmedFeedback());
    } catch (error) {
      setExitError(errorMessage(error));
    } finally {
      setExitBusy(false);
    }
  }
  function cancelExit() {
    setExitDialog(null);
    setExitError(null);
    scannerRef.current?.reset();
  }

  // Chamar acompanhante (urgência)
  const urgencyBlockedUntil = nextAllowedAt ? Date.parse(nextAllowedAt) : null;
  const urgencyBlocked = urgencyBlockedUntil !== null && Number.isFinite(urgencyBlockedUntil) && urgencyBlockedUntil > serverNow;
  async function callCompanionNow(stay: RoomStay) {
    if (busyAction) return;
    setBusyAction("urgency");
    setNotice({});
    try {
      const response = await callCompanion(stay.id);
      setNextAllowedAt(response.nextAllowedAt);
      setNotice(response.queued ? { success: response.message } : { warning: response.message });
    } catch (error) {
      if (isHttpError(error) && error.status === 429) {
        setNotice({
          warning: `${error.message}${nextAllowedAt ? ` Disponível novamente às ${formatTime(nextAllowedAt)}.` : ""}`
        });
      } else {
        setNotice({ error: errorMessage(error, "Não foi possível chamar o acompanhante.") });
      }
    } finally {
      setBusyAction(null);
    }
  }

  // Registrar óbito (PIN do link)
  const [pin, setPin] = useState("");
  const [deathNote, setDeathNote] = useState("");
  const [deathBusy, setDeathBusy] = useState(false);
  const [deathError, setDeathError] = useState<string | null>(null);
  async function registerDeathNow(stay: RoomStay) {
    if (deathBusy) return;
    if (!PIN_PATTERN.test(pin)) {
      setDeathError("Informe o PIN do link (4 a 8 dígitos).");
      return;
    }
    setDeathBusy(true);
    setDeathError(null);
    try {
      const note = deathNote.trim();
      const response = await registerDeath(stay.id, note ? { pin, note } : { pin });
      applyRoom(response.room);
      setDeathDialog(false);
      setPin("");
      setDeathNote("");
      setNotice({ success: "Saída registrada sem mensagem ao acompanhante." });
    } catch (error) {
      setDeathError(
        isHttpError(error) && error.code === "pin_invalid" ? "PIN incorreto. Confira com a coordenação." : errorMessage(error)
      );
    } finally {
      setDeathBusy(false);
    }
  }

  // Finalizar setup
  async function finishSetupNow() {
    if (busyAction) return;
    setBusyAction("setup");
    setNotice({});
    try {
      applyRoom(await finishSetup(roomId));
      setNotice({ success: "Setup finalizado. A sala está livre para a próxima entrada." });
    } catch (error) {
      setNotice({ error: errorMessage(error, "Não foi possível finalizar o setup.") });
    } finally {
      setBusyAction(null);
    }
  }

  // Trocar de sala (PIN validado só no formato; não existe endpoint de verificação)
  const [switchPin, setSwitchPin] = useState("");
  const [switchError, setSwitchError] = useState<string | null>(null);
  function confirmSwitch() {
    if (session.pinRequired && !PIN_PATTERN.test(switchPin)) {
      setSwitchError("Informe o PIN do link (4 a 8 dígitos).");
      return;
    }
    setSwitchDialog(false);
    setSwitchPin("");
    onChangeRoom?.();
  }

  // ------------------------------------------------------------------ render
  if (roomQuery.isPending) {
    return (
      <DeviceShell tag="SALA" title={session.hospitalName} subtitle={session.label}>
        <LoadingState message="Carregando sala…" />
      </DeviceShell>
    );
  }

  if (!room) {
    const notFound = isHttpError(roomQuery.error) && roomQuery.error.status === 404;
    return (
      <DeviceShell tag="SALA" title={session.hospitalName} subtitle={session.label}>
        <ErrorState
          title={notFound ? "Sala não encontrada" : "Não foi possível carregar a sala"}
          message={
            notFound
              ? onChangeRoom
                ? "A sala escolhida foi removida ou desativada. Escolha outra sala."
                : "A sala deste link foi removida ou desativada. Peça um novo link à coordenação."
              : errorMessage(roomQuery.error)
          }
          onRetry={notFound && onChangeRoom ? onChangeRoom : () => void roomQuery.refetch()}
          retryLabel={notFound && onChangeRoom ? "Escolher outra sala" : "Tentar novamente"}
        />
      </DeviceShell>
    );
  }

  const stay = room.openStay;
  const stayMinutes = stay ? minutesBetween(stay.enteredAt, serverNow) : null;
  const setupMinutes = minutesBetween(room.setupStartedAt, serverNow);
  const context = `${room.unitName} · ${room.sectorName} · ${room.name}`;

  return (
    <DeviceShell
      tag="SALA"
      title={room.name}
      subtitle={`${room.unitName} · ${room.sectorName} · ${session.label}`}
      actions={
        <>
          <Badge value={room.state} label={roomStateLabels[room.state]} />
          {onChangeRoom && (
            <button type="button" className="secondary compact" onClick={() => setSwitchDialog(true)}>
              <Repeat aria-hidden="true" size={16} /> Trocar de sala
            </button>
          )}
        </>
      }
    >
      <p className="sr-only" aria-live="polite">
        {context}: {roomStateLabels[room.state]}
      </p>
      <Notice error={notice.error} success={notice.success} warning={notice.warning} />

      {facialEnabled && <ModeSwitch mode={activeMode} onChange={changeMode} />}

      {activeMode === "face" ? (
        <ClinicalFlow
          embedded
          room={{
            id: room.id,
            name: room.name,
            unitId: room.unitId,
            unitName: room.unitName,
            sectorName: room.sectorName
          }}
        />
      ) : (
        <>
      {room.state === "free" && (
        <section className="scan-panel room-panel" aria-labelledby="room-title">
          <h1 id="room-title">
            Pronto para registrar a <em>entrada</em>
          </h1>
          <p>Leia a pulseira do paciente que vai entrar em {room.name}.</p>
          <WristbandScanner
            ref={scannerRef}
            onScan={handleScan}
            hold={dialogOpen}
            idleTitle="Pronto para ler"
            idleDetail="Aproxime a pulseira da câmera ou use o leitor."
          />
        </section>
      )}

      {room.state === "occupied" && stay && (
        <div className="room-stage">
          <section className="login-card patient-card" aria-labelledby="patient-title">
            <span className="eyebrow">Paciente em sala</span>
            <div className="patient-name-row">
              <h2 id="patient-title">{showFullName ? stay.patientName : stay.patientInitials}</h2>
              <button
                type="button"
                className="icon-button"
                onClick={() => setShowFullName((value) => !value)}
                aria-label={showFullName ? "Mostrar apenas iniciais" : "Mostrar nome completo"}
                aria-pressed={!showFullName}
              >
                {showFullName ? <EyeOff aria-hidden="true" size={20} /> : <Eye aria-hidden="true" size={20} />}
              </button>
            </div>
            <dl className="patient-meta">
              <div>
                <dt>Atend.</dt>
                <dd>{stay.wristbandCode}</dd>
              </div>
              <div>
                <dt>Entrada às</dt>
                <dd>{formatTime(stay.enteredAt)}</dd>
              </div>
              <div>
                <dt>Em sala há</dt>
                <dd aria-live="off">{formatDuration(stayMinutes)}</dd>
              </div>
            </dl>
            {stay.alertOpen && (
              <Notice warning="Cadastro incompleto: o acompanhante não está sendo avisado. A coordenação precisa completar o cadastro no painel." />
            )}
            <div className="room-actions">
              <button
                type="button"
                className="primary big-button"
                onClick={() => void callCompanionNow(stay)}
                disabled={busyAction !== null || urgencyBlocked || dialogOpen}
                aria-busy={busyAction === "urgency"}
              >
                <BellRing aria-hidden="true" />
                {busyAction === "urgency" ? "Enviando…" : "Chamar acompanhante"}
              </button>
              {urgencyBlocked && nextAllowedAt && (
                <small role="status">Nova chamada disponível às {formatTime(nextAllowedAt)}.</small>
              )}
              <button
                type="button"
                className="secondary danger big-button"
                onClick={() => {
                  setDeathError(null);
                  setDeathDialog(true);
                }}
                disabled={busyAction !== null || dialogOpen}
              >
                <HeartCrack aria-hidden="true" /> Registrar óbito
              </button>
            </div>
          </section>

          <section className="scan-panel room-panel" aria-labelledby="exit-title">
            <h2 id="exit-title">
              Leia a pulseira para registrar a <em>saída</em>
            </h2>
            <WristbandScanner
              ref={scannerRef}
              onScan={handleScan}
              hold={dialogOpen}
              idleTitle="Pronto para ler"
              idleDetail="A saída pede confirmação antes de ser registrada."
            />
          </section>
        </div>
      )}

      {room.state === "occupied" && !stay && (
        <section className="scan-panel room-panel">
          <AlertTriangle aria-hidden="true" size={48} />
          <h2>Sala ocupada sem permanência aberta</h2>
          <p>Os dados da sala estão inconsistentes. Atualize a tela ou procure a coordenação.</p>
          <button type="button" className="secondary" onClick={() => void refresh()}>
            Atualizar
          </button>
        </section>
      )}

      {room.state === "cleaning" && (
        <section className="scan-panel room-panel" aria-labelledby="setup-title">
          <SprayCan aria-hidden="true" size={56} className="room-panel-icon" />
          <h1 id="setup-title">Sala em setup há {formatDuration(setupMinutes)}</h1>
          <p>Finalize a higienização e libere a sala para a próxima entrada.</p>
          <button
            type="button"
            className="primary big-button"
            onClick={() => void finishSetupNow()}
            disabled={busyAction !== null}
            aria-busy={busyAction === "setup"}
          >
            {busyAction === "setup" ? "Finalizando…" : "Finalizar setup"}
          </button>
        </section>
      )}

      {room.state === "blocked" && (
        <section className="scan-panel room-panel" aria-labelledby="blocked-title">
          <Ban aria-hidden="true" size={56} className="room-panel-icon danger" />
          <h1 id="blocked-title">Sala bloqueada</h1>
          <p>Bloqueio de manutenção definido pela coordenação. Novas entradas não são permitidas até a liberação.</p>
        </section>
      )}

      {(room.state === "preparation" || room.state === "finalization") && (
        <section className="scan-panel room-panel">
          <h1>{roomStateLabels[room.state]}</h1>
          <p>Esta etapa é controlada pelo painel administrativo. A leitura de pulseira volta quando a sala estiver livre.</p>
        </section>
      )}

        </>
      )}

      {nameDialog && (
        <Modal title="Pulseira sem cadastro" onClose={cancelQuickName}>
          <form onSubmit={(event) => void submitQuickName(event)} className="quick-name-form" noValidate>
            <p className="confirm-message">
              O código <strong>{nameDialog.code}</strong> não tem cadastro na recepção. Informe o nome completo do
              paciente para registrar a entrada. O acompanhante não será notificado até o cadastro ser completado.
            </p>
            <label htmlFor="quick-name">
              Nome completo do paciente
              <input
                id="quick-name"
                type="text"
                autoFocus
                autoComplete="off"
                value={quickName}
                onChange={(event) => setQuickName(event.target.value)}
                aria-invalid={quickNameError ? true : undefined}
              />
            </label>
            <Notice error={quickNameError} />
            <div className="form-actions">
              <button type="button" className="secondary" onClick={cancelQuickName} disabled={quickNameBusy}>
                Cancelar
              </button>
              <button type="submit" className="primary" disabled={quickNameBusy} aria-busy={quickNameBusy}>
                {quickNameBusy ? "Registrando…" : "Registrar entrada"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {exitDialog && (
        <ConfirmDialog
          title="Confirmar saída"
          message={
            <>
              Confirmar saída de <strong>{exitDialog.patientInitials}</strong> (atend. {exitDialog.wristbandCode})? A sala
              entra em setup e o acompanhante recebe a mensagem de saída.
            </>
          }
          confirmLabel="Confirmar saída"
          busy={exitBusy}
          error={exitError}
          onConfirm={() => void confirmExitNow()}
          onCancel={cancelExit}
        />
      )}

      {deathDialog && stay && (
        <ConfirmDialog
          title="Registrar óbito"
          danger
          message={
            <>
              Confirma o registro de óbito de <strong>{stay.patientInitials}</strong> (atend. {stay.wristbandCode})? A
              permanência será encerrada <strong>sem</strong> mensagem ao acompanhante e sem mensagem pós-alta.
            </>
          }
          confirmLabel="Registrar óbito"
          busy={deathBusy}
          error={deathError}
          onConfirm={() => void registerDeathNow(stay)}
          onCancel={() => {
            setDeathDialog(false);
            setPin("");
            setDeathNote("");
            setDeathError(null);
          }}
        >
          <div className="pin-fields">
            <label htmlFor="death-pin">
              PIN do link
              <input
                id="death-pin"
                type="password"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={8}
                value={pin}
                onChange={(event) => setPin(event.target.value.replace(/\D/g, ""))}
                autoFocus
              />
            </label>
            <label htmlFor="death-note">
              Observação (opcional)
              <textarea
                id="death-note"
                maxLength={300}
                value={deathNote}
                onChange={(event) => setDeathNote(event.target.value)}
              />
            </label>
          </div>
        </ConfirmDialog>
      )}

      {switchDialog && (
        <ConfirmDialog
          title="Trocar de sala"
          message={
            <>
              Este dispositivo deixará de operar <strong>{room.name}</strong>. Em seguida você escolherá a nova sala.
            </>
          }
          confirmLabel="Trocar de sala"
          error={switchError}
          onConfirm={confirmSwitch}
          onCancel={() => {
            setSwitchDialog(false);
            setSwitchPin("");
            setSwitchError(null);
          }}
        >
          {session.pinRequired && (
            <div className="pin-fields">
              <label htmlFor="switch-pin">
                PIN do link
                <input
                  id="switch-pin"
                  type="password"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]*"
                  maxLength={8}
                  value={switchPin}
                  onChange={(event) => {
                    setSwitchPin(event.target.value.replace(/\D/g, ""));
                    setSwitchError(null);
                  }}
                  autoFocus
                />
              </label>
              <Notice warning="A troca de sala fica registrada neste dispositivo. O PIN é conferido pelo servidor nas ações sensíveis (óbito)." />
            </div>
          )}
        </ConfirmDialog>
      )}
    </DeviceShell>
  );
}
